/**
 * Created by piotr.pozniak@thebeaverhead.com on 12/04/2024
 *
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import sleep from "../../../helpers/sleep";
import { getUploadResourceUrl } from "../../../helpers/url";
import { useUserStore } from "../../../hooks/redux/user";
import { useHistory } from "react-router-dom";
import Avatar from "../theme/untitled/Avatar/Avatar";
import IconButton from "../theme/untitled/IconButton/IconButton";
import Icon from "../theme/untitled/icons/Icon";
import styles from "./OrgSwitchOverlay.module.scss";
import { initialsFromName } from "../templates/PageTemplate/useAccountShellData";

const MIN_VISIBLE_MS = 3000;
const SUCCESS_TOAST_MS = 4000;

const OrgSwitchOverlay = () => {
  const [modalDom] = useState(document.createElement("div"));
  const { user, changeOrganization } = useUserStore();
  const history = useHistory();

  const [cardVisible, setCardVisible] = useState(false);
  const [toast, setToast] = useState(null);
  const cardRef = useRef(null);
  const cardShownAtRef = useRef(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    document.body.appendChild(modalDom);
    return () => document.body.removeChild(modalDom);
  }, []);

  const changingToOrg = useMemo(() => {
    if (!user.switchingToOrgUUID) {
      return null;
    }
    return user.model.organizations.find(
      (i) => i.uuid === user.switchingToOrgUUID
    );
  }, [user.switchingToOrgUUID]);

  const currentOrgName = useMemo(() => {
    if (!user.model) {
      return null;
    }
    return user.model.organizations.find(
      (i) => i.uuid === user.model.current_org_uuid
    )?.name;
  }, [user.model]);

  const clearToastTimer = () => clearTimeout(toastTimerRef.current);

  const startToastTimer = () => {
    clearToastTimer();
    toastTimerRef.current = setTimeout(() => setToast(null), SUCCESS_TOAST_MS);
  };

  const hideCard = () => {
    document.body.style.overflow = "auto";
    setCardVisible(false);
    cardShownAtRef.current = null;
  };

  const afterCardMinVisible = (then) => {
    if (!cardShownAtRef.current) {
      then();
      return;
    }
    const remaining = MIN_VISIBLE_MS - (Date.now() - cardShownAtRef.current);
    if (remaining <= 0) {
      then();
    } else {
      sleep(remaining).then(then);
    }
  };

  useEffect(() => {
    if (!user.switchOrganization) {
      return;
    }
    setToast(null);
    document.body.style.overflow = "hidden";
    cardShownAtRef.current = Date.now();
    setCardVisible(true);
  }, [user.switchOrganization]);

  useEffect(() => {
    if (!user.switchOrganizationSuccess) {
      return;
    }
    history.push("/dashboard");
    afterCardMinVisible(() => {
      hideCard();
      setToast({ variant: "success", name: changingToOrg?.name });
      startToastTimer();
    });
  }, [user.switchOrganizationSuccess]);

  useEffect(() => {
    if (!user.switchOrganizationError) {
      return;
    }
    afterCardMinVisible(() => {
      hideCard();
      clearToastTimer();
      setToast({
        variant: "error",
        name: changingToOrg?.name,
        currentName: currentOrgName,
      });
    });
  }, [user.switchOrganizationError]);

  useEffect(() => {
    if (!cardVisible) {
      return;
    }
    cardRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [cardVisible]);

  const onDismissToast = useCallback(() => {
    clearToastTimer();
    setToast(null);
  }, []);

  const onRetry = useCallback(() => {
    setToast(null);
    changeOrganization(user.switchingToOrgUUID);
  }, [changeOrganization, user.switchingToOrgUUID]);

  const card = cardVisible && changingToOrg && (
    <div className={styles.scrim}>
      <div
        className={styles.card}
        role="status"
        aria-live="polite"
        ref={cardRef}
        tabIndex={-1}
      >
        <Avatar
          size="lg"
          src={
            changingToOrg.settings?.favicon
              ? getUploadResourceUrl(changingToOrg.settings.favicon)
              : null
          }
          initials={initialsFromName(changingToOrg.name)}
          alt={`${changingToOrg.name} logo`}
        />
        <div className={styles.textGroup}>
          <p className={styles.title}>Switching to {changingToOrg.name}</p>
          <p className={styles.supporting}>Loading its settings and widgets.</p>
        </div>
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-label="Switching workspace"
        >
          <div className={styles.progressFill} />
        </div>
      </div>
    </div>
  );

  const successToast = toast?.variant === "success" && (
    <div
      className={styles.toast}
      role="status"
      onMouseEnter={clearToastTimer}
      onMouseLeave={startToastTimer}
      onFocus={clearToastTimer}
      onBlur={startToastTimer}
    >
      <span
        className={classNames(styles.toastDisc, styles["toastDisc--success"])}
      >
        <Icon name="CheckCircle" size={20} />
      </span>
      <div className={styles.toastBody}>
        <p className={styles.toastTitle}>Switched to {toast.name}</p>
      </div>
      <span className={styles.toastDismiss}>
        <IconButton
          size="sm"
          variant="ghost"
          icon={<Icon name="XClose" />}
          label="Dismiss"
          onClick={onDismissToast}
        />
      </span>
    </div>
  );

  const errorToast = toast?.variant === "error" && (
    <div className={styles.toast} role="alert">
      <span
        className={classNames(styles.toastDisc, styles["toastDisc--error"])}
      >
        <Icon name="AlertCircle" size={20} />
      </span>
      <div className={styles.toastBody}>
        <p className={styles.toastTitle}>Couldn’t switch to {toast.name}</p>
        <p className={styles.toastText}>
          You’re still in {toast.currentName}. Check your connection and try
          again.
        </p>
        <div className={styles.toastActions}>
          <button
            type="button"
            className={classNames(
              styles.toastAction,
              styles["toastAction--dismiss"]
            )}
            onClick={onDismissToast}
          >
            Dismiss
          </button>
          <button
            type="button"
            className={classNames(
              styles.toastAction,
              styles["toastAction--retry"]
            )}
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      </div>
      <span className={styles.toastDismiss}>
        <IconButton
          size="sm"
          variant="ghost"
          icon={<Icon name="XClose" />}
          label="Dismiss"
          onClick={onDismissToast}
        />
      </span>
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {card}
      {successToast}
      {errorToast}
    </>,
    modalDom
  );
};

export default OrgSwitchOverlay;
